import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowUpRight, Menu, X, Mail } from "lucide-react";
import { SITE } from "@/data/site";
import { useLang } from "@/i18n/LanguageContext";

const LINK_IDS = [
  { to: "/", key: "home", id: "home" },
  { to: "/about", key: "story", id: "our-story" },
  { to: "/services", key: "services", id: "produce-services" },
  { to: "/gallery", key: "gallery", id: "gallery" },
  { to: "/contact", key: "contact", id: "contact" },
];

const LangToggle = ({ className = "" }) => {
  const { lang, setLang } = useLang();
  return (
    <div className={`flex items-center rounded-full border border-[var(--line)] bg-white/70 p-0.5 font-mono-label text-[10px] ${className}`} data-testid="lang-toggle">
      {["en", "es"].map((l) => (
        <button
          key={l}
          onClick={() => setLang(l)}
          data-testid={`lang-toggle-${l}`}
          aria-pressed={lang === l}
          className={`rounded-full px-2.5 py-1.5 transition-colors ${lang === l ? "bg-[var(--green)] text-white" : "text-[var(--ink-2)] hover:text-[var(--ink)]"}`}
        >
          {l.toUpperCase()}
        </button>
      ))}
    </div>
  );
};

export const Nav = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { pathname } = useLocation();
  const { t } = useLang();
  const LINKS = LINK_IDS.map((l) => ({ ...l, label: t.nav[l.key] }));

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [pathname]);

  return (
    <header className="fixed inset-x-0 top-0 z-50" data-testid="site-header">
      <div className="hidden md:block bg-[var(--ink)] text-white/80">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-6 py-2 font-mono-label text-[10.5px] lg:px-10">
          <span data-testid="topbar-delivery">{t.nav.topbar}</span>
          <a href={`mailto:${SITE.email}`} className="flex items-center gap-2 hover:text-white transition-colors" data-testid="topbar-email">
            <Mail size={12} /> {SITE.email}
          </a>
        </div>
      </div>
      <div
        className={`transition-colors duration-500 ${
          scrolled || open ? "bg-[rgba(228,242,219,0.92)] backdrop-blur-xl border-b border-[var(--line)]" : "bg-transparent"
        }`}
      >
        <nav className="mx-auto flex max-w-7xl items-center justify-between px-6 py-3 lg:px-10">
          <Link to="/" className="flex items-center gap-3" data-testid="nav-logo-link">
            <img src="/logo.png" alt="Sebastian's Produce" className="h-14 w-auto drop-shadow-sm" />
            <span className="hidden sm:block font-mono-label text-[10px] text-[var(--muted)]">{t.site.est}</span>
          </Link>

          <ul className="hidden items-center gap-8 lg:flex">
            {LINKS.map((l) => (
              <li key={l.to}>
                <NavLink
                  to={l.to}
                  data-testid={`nav-link-${l.id}`}
                  className={({ isActive }) =>
                    `link-underline text-[13.5px] font-medium transition-colors ${
                      isActive ? "text-[var(--green)]" : "text-[var(--ink)]/80 hover:text-[var(--ink)]"
                    }`
                  }
                >
                  {l.label}
                </NavLink>
              </li>
            ))}
          </ul>

          <div className="flex items-center gap-3">
            <LangToggle />
            <Link to="/contact" className="btn-ink hidden sm:inline-flex !py-2.5 !px-5" data-testid="nav-cta-quote">
              {t.nav.quote} <ArrowUpRight size={16} />
            </Link>
            <button
              className="lg:hidden rounded-full border border-[var(--line)] p-2.5"
              onClick={() => setOpen((v) => !v)}
              aria-label="Toggle menu"
              data-testid="nav-mobile-toggle"
            >
              {open ? <X size={18} /> : <Menu size={18} />}
            </button>
          </div>
        </nav>
        <AnimatePresence>
          {open && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: "auto", opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
              className="overflow-hidden lg:hidden"
              data-testid="nav-mobile-menu"
            >
              <ul className="flex flex-col gap-1 px-6 pb-6 pt-2">
                {LINKS.map((l) => (
                  <li key={l.to}>
                    <NavLink to={l.to} className="font-display block py-2 text-2xl font-semibold" data-testid={`nav-mobile-link-${l.id}`}>
                      {l.label}
                    </NavLink>
                  </li>
                ))}
                <li className="pt-3">
                  <Link to="/contact" className="btn-primary" data-testid="nav-mobile-cta-quote">
                    {t.nav.quote} <ArrowUpRight size={16} />
                  </Link>
                </li>
              </ul>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </header>
  );
};
